import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { toPng } from 'html-to-image';
import { toast } from 'sonner';
import {
  Sparkles,
  BookOpen,
  Zap,
  Shield,
  Users,
  Heart,
  Trophy,
  MessageCircle,
  Flame,
  CheckCircle2,
  Printer,
  Download
} from 'lucide-react';

const MASCOT =
  'https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png';

const FlyerPage = () => {
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    document.title = 'Prof.Scaly — PSLE English Flyer';
  }, []);

  const handlePrint = () => window.print();

  const handleDownloadPng = async () => {
    const node = document.getElementById('flyer');
    if (!node) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(node, {
        cacheBust: true,
        pixelRatio: 2, // ~1600px wide for crisp printing
        backgroundColor: '#FFFFFF',
        style: { boxShadow: 'none' }
      });
      const link = document.createElement('a');
      link.download = `prof-scaly-flyer-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = dataUrl;
      link.click();
      toast.success('Flyer downloaded as PNG!');
    } catch (err) {
      console.error(err);
      toast.error('Could not export as PNG. Try again in a moment.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 py-8 print:py-0 print:bg-white">
      {/* Toolbar (hidden when printing) */}
      <div className="max-w-[900px] mx-auto px-4 mb-4 flex items-center justify-between print:hidden">
        <div className="text-sm text-slate-600">
          <strong>Marketing flyer</strong> — download as PNG (best for social/WhatsApp) or PDF (best for print).
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownloadPng}
            disabled={downloading}
            className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white text-sm font-bold px-4 py-2 rounded-full inline-flex items-center gap-1.5 transition-colors"
            data-testid="flyer-download-png"
          >
            <Download size={14} /> {downloading ? 'Preparing…' : 'Download PNG'}
          </button>
          <button
            onClick={handlePrint}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-2 rounded-full inline-flex items-center gap-1.5"
            data-testid="flyer-print"
          >
            <Printer size={14} /> Print / Save PDF
          </button>
        </div>
      </div>

      {/* Flyer canvas — A4 portrait 210x297mm at ~96dpi ≈ 794x1123px */}
      <article
        id="flyer"
        className="mx-auto bg-white shadow-2xl print:shadow-none relative overflow-hidden"
        style={{ width: '794px', minHeight: '1123px' }}
        data-testid="flyer-canvas"
      >
        {/* Top orange band */}
        <div
          className="relative px-10 pt-10 pb-8 text-white overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #F97316 0%, #F59E0B 55%, #FDBA74 100%)' }}
        >
          <div className="absolute -right-8 -top-8 w-64 h-64 rounded-full opacity-20" style={{ background: '#FFF' }} />
          <div className="absolute right-6 top-4 rotate-6">
            <img src={MASCOT} alt="Prof.Scaly" className="w-52 h-52 object-contain drop-shadow-2xl" />
          </div>
          <div className="relative max-w-[440px]">
            <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur border border-white/40 rounded-full px-3 py-1 text-xs font-bold mb-3">
              <Sparkles size={12} /> Aligned to Singapore MOE syllabus
            </div>
            <div className="text-[13px] font-black uppercase tracking-widest text-emerald-100 mb-1">
              Prof.Scaly · PSLE English Coach
            </div>
            <h1 className="font-heading font-black leading-[1.05] mb-3" style={{ fontSize: '58px' }}>
              Make PSLE<br />English <span className="text-emerald-200">fun again.</span>
            </h1>
            <p className="text-orange-50 text-base leading-relaxed max-w-[380px]">
              A friendly AI tutor for Primary 4, 5 &amp; 6 students in Singapore. Nine PSLE English modules, instant kid-friendly explanations, and a game world that turns practice into a mission for a cleaner Singapore.
            </p>
          </div>
        </div>

        {/* Why parents choose us — three bold value props */}
        <section className="px-10 py-6 border-b border-slate-200">
          <div className="grid grid-cols-3 gap-4">
            {[
              { icon: BookOpen, title: '9 PSLE modules', body: 'Every question type in the paper — plus dedicated Antonyms & Synonyms and Regular & Irregular Verbs drills.', tint: 'text-emerald-700', bg: 'bg-emerald-50' },
              { icon: Zap, title: 'Instant AI tutor', body: 'Powered by GPT-5.2. Every wrong answer comes with a Prof.Scaly explanation in simple language.', tint: 'text-amber-700', bg: 'bg-amber-50' },
              { icon: Shield, title: 'Track sub-skills', body: 'Progress is tracked topic-by-topic AND per sub-skill so you know exactly where to focus revision.', tint: 'text-indigo-700', bg: 'bg-indigo-50' }
            ].map((v, i) => (
              <div key={i} className={`${v.bg} rounded-2xl p-4`}>
                <v.icon size={22} className={v.tint} />
                <div className={`font-heading font-black text-base ${v.tint} mt-1.5 mb-1`}>{v.title}</div>
                <p className="text-xs text-slate-700 leading-snug">{v.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 9 modules row */}
        <section className="px-10 py-6 border-b border-slate-200">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen size={18} className="text-emerald-600" />
            <h2 className="font-heading font-black text-lg text-slate-900">Every PSLE English topic, one app</h2>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { name: 'Grammar Cloze', bg: '#D1FAE5', border: '#6EE7B7' },
              { name: 'Vocabulary Cloze', bg: '#FEF3C7', border: '#FCD34D' },
              { name: 'Grammar MCQ', bg: '#E0E7FF', border: '#A5B4FC' },
              { name: 'Vocabulary MCQ', bg: '#FFE4E6', border: '#FDA4AF' },
              { name: 'Synthesis & Transformation', bg: '#F3E8FF', border: '#D8B4FE' },
              { name: 'Editing', bg: '#DBEAFE', border: '#93C5FD' },
              { name: 'Comprehension Cloze', bg: '#FCE7F3', border: '#F9A8D4' },
              { name: 'Antonyms & Synonyms', bg: '#CCFBF1', border: '#5EEAD4' },
              { name: 'Regular & Irregular Verbs', bg: '#FED7AA', border: '#FDBA74' }
            ].map((m) => (
              <div
                key={m.name}
                className="rounded-xl border-2 px-3 py-2.5 text-center font-heading font-bold text-slate-800 text-[13px]"
                style={{ backgroundColor: m.bg, borderColor: m.border }}
              >
                {m.name}
              </div>
            ))}
          </div>
        </section>

        {/* Fight for Singapore section */}
        <section className="px-10 py-6 border-b border-slate-200 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #FFF7ED, #FFEDD5)' }}>
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <div className="inline-flex items-center gap-1 bg-orange-500 text-white text-[10px] font-black uppercase tracking-widest rounded-full px-2.5 py-1 mb-2">
                <Flame size={10} /> Game world · Play & Learn
              </div>
              <h2 className="font-heading font-black text-2xl text-slate-900 mb-2 leading-tight">
                Fight for Singapore
              </h2>
              <p className="text-sm text-slate-700 leading-relaxed mb-3 max-w-[420px]">
                Every correct answer weakens a monster. Defeat all three to help keep Singapore <strong>clean</strong>, <strong>green</strong> and <strong>plastic-free</strong>. Practice becomes a purposeful mission — not a chore.
              </p>
              <div className="flex gap-3 text-xs text-slate-700">
                <div className="flex items-center gap-1"><Trophy size={12} className="text-amber-600" /> 5 badges to earn</div>
                <div className="flex items-center gap-1"><Users size={12} className="text-emerald-600" /> Community counter</div>
                <div className="flex items-center gap-1"><Flame size={12} className="text-orange-600" /> Daily streaks</div>
              </div>
            </div>
            <div className="flex gap-1">
              <img src="/monsters/dirty.jpg" alt="Dirty Monster" className="w-20 h-20 object-contain rounded-lg" />
              <img src="/monsters/grey.jpg" alt="Grey Monster" className="w-20 h-20 object-contain rounded-lg" />
              <img src="/monsters/plastic.jpg" alt="Plastic Monster" className="w-20 h-20 object-contain rounded-lg" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3 text-[10px] text-slate-600 text-center">
            <div><strong className="text-slate-900">Keep Singapore Clean</strong><br />vs The Dirty Monster</div>
            <div><strong className="text-slate-900">Keep Singapore Green</strong><br />vs The Grey Monster</div>
            <div><strong className="text-slate-900">Keep Waters Plastic-Free</strong><br />vs The Plastic Monster</div>
          </div>
        </section>

        {/* Feature list + Chatbot */}
        <section className="px-10 py-6 border-b border-slate-200 grid grid-cols-2 gap-6">
          <div>
            <h2 className="font-heading font-black text-base text-slate-900 mb-3">What your child gets</h2>
            <ul className="space-y-1.5 text-[13px] text-slate-800">
              {[
                'Unlimited AI-generated PSLE questions at P4, P5 or P6',
                'Kid-friendly "Explain Further" tutor chat',
                'Daily streak system builds a revision habit',
                'Sub-skill progress: Synonyms vs Antonyms, Regular vs Irregular Verbs',
                'Save favourites for quick revision before exams',
                'Teachers can upload custom question banks'
              ].map((line) => (
                <li key={line} className="flex items-start gap-2">
                  <CheckCircle2 size={14} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageCircle size={16} className="text-emerald-700" />
              <h3 className="font-heading font-black text-sm text-emerald-800">Chat with Prof.Scaly</h3>
            </div>
            <p className="text-[12px] text-slate-700 leading-relaxed mb-2">
              A floating AI tutor bubble your child can tap any time to ask English questions or get help using the app. Friendly Singaporean-teacher tone — never off-topic, never off-syllabus.
            </p>
            <div className="text-[10px] italic text-emerald-800 bg-white/70 rounded-lg p-2 border border-emerald-200">
              "Affect = Action (verb) ✅  Effect = End result (noun) ✅ — a simple trick to remember them! 🐢"
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="px-10 py-6 border-b border-slate-200">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl border-2 border-slate-300 p-4">
              <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">FREE tier</div>
              <div className="font-heading font-black text-3xl text-slate-900 mt-1">$0</div>
              <p className="text-xs text-slate-600 mt-1 mb-2">50 free practices per topic. Try every module.</p>
              <div className="text-[10px] text-slate-500">All 9 modules · AI tutor · Streaks · Progress</div>
            </div>
            <div className="rounded-2xl border-2 border-emerald-500 bg-emerald-50 p-4 relative">
              <div className="absolute -top-2 right-3 bg-emerald-600 text-white text-[9px] font-black px-2 py-0.5 rounded-full">RECOMMENDED</div>
              <div className="text-[10px] font-black text-emerald-700 uppercase tracking-widest">PREMIUM</div>
              <div className="font-heading font-black text-3xl text-slate-900 mt-1">
                SGD $2<span className="text-sm text-slate-500 font-bold">/year/level</span>
              </div>
              <p className="text-xs text-slate-700 mt-1 mb-2">Unlimited practice. Less than a plate of chicken rice.</p>
              <div className="text-[10px] text-emerald-800">Everything in Free · Unlimited questions · Priority support</div>
            </div>
          </div>
        </section>

        {/* CTA banner + footer */}
        <section className="px-10 py-6 text-white relative overflow-hidden" style={{ background: 'linear-gradient(90deg, #10B981 0%, #14B8A6 100%)' }}>
          <div className="flex items-center justify-between gap-6">
            <div className="flex-1">
              <h2 className="font-heading font-black text-2xl leading-tight mb-1">Ready to help your child ace PSLE English?</h2>
              <p className="text-emerald-50 text-sm mb-2">Sign up in 30 seconds — no credit card required. First 50 questions per topic are on us.</p>
              <div className="bg-white text-emerald-700 rounded-2xl px-5 py-2.5 shadow-lg inline-block">
                <div className="text-[10px] font-black uppercase tracking-widest text-emerald-600">Start free at</div>
                <div className="font-heading font-black text-xl leading-none mt-0.5">prof-scaly.com</div>
              </div>
              <div className="text-[10px] text-emerald-100 mt-2 flex items-center gap-1">
                <Heart size={10} /> Built with love for Singapore families
              </div>
            </div>
            <div className="flex-shrink-0 text-center">
              <div className="bg-white p-2.5 rounded-2xl shadow-lg" data-testid="flyer-qr">
                <QRCodeSVG
                  value="https://prof-scaly.com/register?utm_source=flyer"
                  size={112}
                  level="M"
                  includeMargin={false}
                  fgColor="#065F46"
                  bgColor="#FFFFFF"
                />
              </div>
              <div className="text-[10px] font-bold text-emerald-50 mt-1.5 leading-tight">
                Scan to sign up<br />
                <span className="text-emerald-100 font-normal">Free · 30 seconds</span>
              </div>
            </div>
          </div>
        </section>

        {/* Bottom strip */}
        <footer className="px-10 py-3 bg-slate-900 text-slate-400 text-[10px] flex items-center justify-between">
          <div>© {new Date().getFullYear()} Prof.Scaly · PSLE English tutoring for Primary 4-6</div>
          <div className="flex items-center gap-3">
            <span>Aligned to MOE syllabus</span>
            <span>·</span>
            <span>hello@prof-scaly.com</span>
          </div>
        </footer>
      </article>

      {/* Print styles */}
      <style>{`
        /* Force browsers to keep background colours and images when printing / saving to PDF */
        #flyer, #flyer * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        @media print {
          @page { size: A4 portrait; margin: 0; }
          html, body { margin: 0; padding: 0; background: #fff; }
          body * { visibility: hidden; }
          #flyer, #flyer * { visibility: visible; }
          #flyer {
            box-shadow: none !important;
            width: 210mm !important;
            min-height: 297mm !important;
            position: absolute;
            left: 0;
            top: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default FlyerPage;
